import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2, Loader2, AlertTriangle, RefreshCw, ArrowUpRight, ChevronDown, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HandoffStatusCard
 *
 * Stage 3 (Wizard → Parent app webhook) is fired fire-and-forget at submit
 * time so the RN doesn't wait on a network round-trip. But she still needs
 * to know whether the bundle made it. This card polls
 * /api/wizard/{job_id}/handoff-status until delivered (or until we hit a
 * terminal failed state) and shows:
 *   • Idle / no-visit  — quietly hidden (no parent app integrated)
 *   • Pending         — spinner, "Delivering to parent app…"
 *   • Delivered       — green check + delivered_at timestamp
 *   • Failed          — orange warning + "Retry" button that POSTs the
 *                       handoff endpoint again and resumes polling
 */
export default function HandoffStatusCard({ jobId, visitId, callbackUrlOverride }) {
  const [status, setStatus] = useState({ phase: "loading", attempts: 0, delivered_at: null, last_error: "" });
  const [retrying, setRetrying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attemptsLog, setAttemptsLog] = useState([]);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!jobId) return;
    try {
      const r = await axios.get(`${API}/wizard/${jobId}/handoff-status`);
      const summary = r.data?.summary || {};
      const last = r.data?.last_attempt;
      const phase = summary.delivered === true
        ? "delivered"
        : summary.last_error
          ? "failed"
          : "pending";
      setStatus({
        phase,
        attempts: summary.attempts || 0,
        delivered_at: summary.delivered_at || null,
        last_error: summary.last_error || "",
        callback_url: summary.callback_url || "",
      });
      setAttemptsLog(last?.attempts || []);
    } catch (e) {
      // If status endpoint doesn't exist yet (e.g. Stage 3 hasn't fired
      // even once), treat as silent no-op rather than erroring loudly.
      if (e.response?.status !== 404) {
        setStatus((s) => ({ ...s, phase: "failed", last_error: e.message }));
      } else {
        setStatus((s) => ({ ...s, phase: "idle" }));
      }
    }
  }, [jobId]);

  // Poll until terminal — every 3s for up to 60s after mount.
  useEffect(() => {
    if (!jobId) return undefined;
    fetchStatus();
    const start = Date.now();
    pollRef.current = setInterval(() => {
      if (Date.now() - start > 60_000 || ["delivered", "failed", "idle"].includes(status.phase)) {
        clearInterval(pollRef.current);
        return;
      }
      fetchStatus();
    }, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const retry = async () => {
    if (!jobId) return;
    setRetrying(true);
    try {
      const body = visitId ? { visit_id: visitId } : { callback_url_override: callbackUrlOverride };
      // Endpoint is now async (202 Accepted). The actual delivery runs as a
      // BackgroundTask; we poll /handoff-status to learn the outcome.
      await axios.post(`${API}/wizard/${jobId}/handoff`, body, { timeout: 30000 });
      toast.success("Handoff re-queued — checking status…");
      // Reset to pending and resume the polling loop.
      setStatus((s) => ({ ...s, phase: "pending", last_error: "" }));
      setAttemptsLog([]);
      // Trigger an immediate poll; the existing useEffect interval covers the rest.
      await fetchStatus();
    } catch (e) {
      toast.error(`Retry failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRetrying(false);
    }
  };

  if (status.phase === "idle") return null;  // No parent app wired

  const tone = status.phase === "delivered"
    ? { bg: "bg-[#d9e6df]", border: "border-[#0A3D79]", icon: CheckCircle2, color: "text-[#0A3D79]" }
    : status.phase === "failed"
      ? { bg: "bg-[#fbeae5]", border: "border-[#e2b6a9]", icon: AlertTriangle, color: "text-[#c05746]" }
      : { bg: "bg-[#E7F1FB]", border: "border-[#C1D7EF]", icon: Loader2, color: "text-[#0A3D79]" };
  const Icon = tone.icon;
  const label = status.phase === "delivered"
    ? `Delivered to parent app ${status.delivered_at ? `at ${new Date(status.delivered_at).toLocaleTimeString()}` : ""}`
    : status.phase === "failed"
      ? `Handoff failed — ${status.last_error || "no parent response"}`
      : "Delivering bundle to parent app…";

  return (
    <div
      className={`mt-6 rounded-xl border ${tone.border} ${tone.bg} px-5 py-4`}
      data-testid="handoff-status-card"
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${tone.color} ${status.phase === "pending" ? "animate-spin" : ""} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-[15px] font-semibold text-[#0B1E3A]" data-testid="handoff-status-label">
              {label}
            </span>
            {status.attempts > 0 && (
              <span className="text-[11.5px] text-[#3A5275]">
                · {status.attempts} attempt{status.attempts === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {status.callback_url && (
            <div className="text-[12px] text-[#3A5275] mt-0.5 truncate flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" />
              <span className="font-mono" data-testid="handoff-callback-url">{status.callback_url}</span>
            </div>
          )}
          {attemptsLog.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="text-[11.5px] font-semibold text-[#0A3D79] mt-1 inline-flex items-center gap-1 hover:underline"
              data-testid="handoff-attempts-toggle"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {expanded ? "Hide" : "Show"} attempt history
            </button>
          )}
          {expanded && (
            <ul className="mt-2 space-y-1 text-[11.5px] text-[#3A5275]" data-testid="handoff-attempts-list">
              {attemptsLog.map((a) => (
                <li key={a.attempt} className="font-mono">
                  #{a.attempt} · {a.completed_at ? new Date(a.completed_at).toLocaleTimeString() : "—"}
                  {" · "}
                  {a.status_code ? `HTTP ${a.status_code}` : `error: ${a.error || "—"}`}
                </li>
              ))}
            </ul>
          )}
        </div>
        {status.phase === "failed" && (
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            className="btn-primary !h-9 !px-3 !text-[12.5px] flex-shrink-0"
            data-testid="handoff-retry"
          >
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Retry handoff
          </button>
        )}
      </div>
    </div>
  );
}
